import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Phone, PhoneOff, Mic, MicOff, X, Pause, Play, AlertTriangle } from 'lucide-react';
import { getWebRTCSIPConfig, getAllLeads, getCampaignsBasedOnUser } from '../../services/endpoint';
import JsSIP from 'jssip';
import { useNavigate } from 'react-router-dom';
import { useCallMode } from '../../context/CallModeContext';
import { toE164 } from '../../utils/tools';
import toneGenerator from './toneGenerator';

// ─── Error catalogue (shared shape with TeleCMIDialer) ────────────────────────
const ERROR_META = {
  mic_blocked: {
    color: 'orange',
    title: 'Microphone blocked',
    body: 'Allow microphone access in your browser settings, then dismiss and retry.',
    canDismiss: true,
  },
  no_mic: {
    color: 'orange',
    title: 'No microphone found',
    body: 'Plug in a microphone or headset, then dismiss and retry.',
    canDismiss: true,
  },
  auth_failed: {
    color: 'red',
    title: 'Authentication failed',
    body: 'SIP credentials are invalid. Contact your administrator to update them.',
    canDismiss: true,
  },
  config_error: {
    color: 'red',
    title: 'Configuration error',
    body: 'Could not load SIP settings. Please refresh the page.',
    canDismiss: false,
    canRetry: true,
  },
  disconnected: {
    color: 'yellow',
    title: 'Reconnecting…',
    body: 'Lost connection to the SIP server. Trying to reconnect.',
    canDismiss: false,
  },
  call_failed: {
    color: 'yellow',
    title: 'Call failed',
    body: 'The call could not be connected. Check the number and try again.',
    canDismiss: true,
  },
  busy: {
    color: 'yellow',
    title: 'Line busy',
    body: 'The called party is busy. Try again in a moment.',
    canDismiss: true,
  },
  rejected: {
    color: 'yellow',
    title: 'Call rejected',
    body: 'The called party rejected the call.',
    canDismiss: true,
  },
  invalid_number: {
    color: 'yellow',
    title: 'Invalid number',
    body: 'Enter a valid phone number including country code (e.g. +1 555 000 0000).',
    canDismiss: true,
  },
};

// ─── Mic verification helper ──────────────────────────────────────────────────
async function checkMicAccess() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    stream.getTracks().forEach(t => t.stop());
    return null; // mic fine
  } catch (err) {
    const name = err?.name || '';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') return 'mic_blocked';
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'no_mic';
    return 'mic_blocked';
  }
}

// ─── Phone input sanitiser ────────────────────────────────────────────────────
function sanitisePhoneInput(raw) {
  if (!raw) return '';
  const hasPlus = raw.startsWith('+');
  const body = raw.replace(/[^\d#*]/g, '');
  return hasPlus ? '+' + body : body;
}

function isCallableNumber(num) {
  const digits = num.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

// ─── Banner ───────────────────────────────────────────────────────────────────
function ErrorBanner({ type, onDismiss, onRetry }) {
  const meta = ERROR_META[type];
  if (!meta) return null;
  const colorMap = {
    red:    { bg: 'bg-red-50',    border: 'border-red-200',    icon: 'text-red-500',    text: 'text-red-700',    btn: 'text-red-600 hover:text-red-800' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-500', text: 'text-orange-700', btn: 'text-orange-600 hover:text-orange-800' },
    yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'text-yellow-600', text: 'text-yellow-700', btn: 'text-yellow-600 hover:text-yellow-800' },
  };
  const c = colorMap[meta.color] || colorMap.red;
  return (
    <div className={`mx-4 mt-2 px-3 py-2 ${c.bg} border ${c.border} rounded-lg`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`w-4 h-4 ${c.icon} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold ${c.text}`}>{meta.title}</p>
          <p className={`text-xs ${c.text} opacity-80 mt-0.5`}>{meta.body}</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {meta.canRetry && onRetry && (
            <button onClick={onRetry} className={`${c.btn} text-xs underline whitespace-nowrap`}>Retry</button>
          )}
          {meta.canDismiss && (
            <button onClick={onDismiss} className={`${c.btn} text-xs underline whitespace-nowrap`}>Dismiss</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const DialerWidget = ({ phoneNumber = '', embedded = false, isHost = false }) => {
  const navigate = useNavigate();
  const {
    callMode,
    callStatus: ctxCallStatus,
    callNumber: ctxCallNumber,
    callDuration: ctxCallDuration,
    callMuted: ctxCallMuted,
    isIncoming: ctxIsIncoming,
    setCallStatus: setCtxStatus,
    setCallNumber: setCtxNumber,
    setCallDuration: setCtxDuration,
    setCallMuted: setCtxMuted,
    setIsIncoming: setCtxIsIncoming,
    activeSessionRef: ctxSessionRef,
    dialerActionsRef,
  } = useCallMode();
  const canOutbound = callMode === 'Outbound' || callMode === 'Blended';
  const canInboundRef = useRef(true);

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState('offline');
  const [errorType, setErrorType] = useState(null);
  const [number, setNumber] = useState('');
  const [inCall, setInCall] = useState(false);
  const [showDialpad, setShowDialpad] = useState(false);
  const [muted, setMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [incoming, setIncoming] = useState(false);
  const [incomingFrom, setIncomingFrom] = useState('');
  const [onHold, setOnHold] = useState(false);

  // Lead lookup / confirmation
  const [leadLookup, setLeadLookup] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [leadConfirmed, setLeadConfirmed] = useState(false);
  const confirmedNumberRef = useRef('');
  const lookupTimerRef = useRef(null);

  const uaRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const pendingRemoteTrackRef = useRef(null);
  const sessionRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const localStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const statusRef = useRef('offline');
  const activeUaRef = useRef(null);
  const mountIdRef = useRef(0);
  const navigatingRef = useRef(false);
  const configRef = useRef(null);

  // ── Keep refs in sync ──────────────────────────────────────────────────────
  useEffect(() => { canInboundRef.current = callMode === 'Inbound' || callMode === 'Blended'; }, [callMode]);
  useEffect(() => { if (isHost) setCtxStatus(status); }, [isHost, status, setCtxStatus]);
  useEffect(() => { if (isHost) setCtxNumber(number); }, [isHost, number, setCtxNumber]);
  useEffect(() => { if (isHost) setCtxDuration(callDuration); }, [isHost, callDuration, setCtxDuration]);
  useEffect(() => { if (isHost) setCtxMuted(muted); }, [isHost, muted, setCtxMuted]);
  useEffect(() => { if (isHost) setCtxIsIncoming(incoming); }, [isHost, incoming, setCtxIsIncoming]);
  useEffect(() => { if (isHost) ctxSessionRef.current = sessionRef.current; });
  useEffect(() => { configRef.current = config; }, [config]);

  // ── Auto-clear false-positive mic errors ───────────────────────────────────
  useEffect(() => {
    if (errorType !== 'mic_blocked' && errorType !== 'no_mic') return;
    const timer = setTimeout(async () => {
      const result = await checkMicAccess();
      if (!result) { setErrorType(null); setStatus('online'); }
    }, 5000);
    return () => clearTimeout(timer);
  }, [errorType]);

  // ── Sync phoneNumber prop ──────────────────────────────────────────────────
  useEffect(() => {
    if (!phoneNumber) return;
    if (inCall || incoming) return;
    // When the parent loads a new lead with a different number, reset confirmation
    // so the softphone shows the fresh lead's number without a full page refresh.
    if (leadConfirmed && phoneNumber !== confirmedNumberRef.current) {
      setLeadConfirmed(false);
      confirmedNumberRef.current = '';
      setLeadLookup(null);
    }
    // Always set the number when phoneNumber prop changes and not in a call
    setNumber(phoneNumber);
  }, [phoneNumber]);

  // ── Lead lookup ────────────────────────────────────────────────────────────
  const lookupLead = useCallback(async (num) => {
    const clean = num.replace(/[^\d+]/g, '');
    if (clean.length < 7) { setLeadLookup(null); return; }
    setLookupLoading(true);
    try {
      const [leadsRes, campaignsRes] = await Promise.allSettled([
        getAllLeads({ page: 1, search: clean, campaign: '', status: '', lead_group: '', limit: 1, exclude_terminal: true }),
        getCampaignsBasedOnUser(),
      ]);
      const leads = leadsRes.status === 'fulfilled'
        ? (leadsRes.value?.data?.results || leadsRes.value?.data?.data || [])
        : [];
      const campaigns = campaignsRes.status === 'fulfilled'
        ? (campaignsRes.value?.data?.data || campaignsRes.value?.data?.results || [])
        : [];
      const firstCampaignId = campaigns[0]?.id || null;
      const firstCampaignName = campaigns[0]?.name || '';
      const openLead = leads.find(l => !['converted', 'dropped', 'duplicate', 'exhausted'].includes(l.status));
      if (openLead) {
        const leadCampaign = campaigns.find(c => c.id === openLead.campaign);
        setLeadLookup({ found: true, lead: openLead, campaign_id: openLead.campaign || firstCampaignId, campaign_name: leadCampaign?.name || firstCampaignName });
      } else {
        // Preserve + as-is; no hard-coded country fallback
        const e164 = toE164(clean);
        setLeadLookup({ found: false, lead: null, campaign_id: firstCampaignId, campaign_name: firstCampaignName, phone: e164 });
      }
    } catch {
      setLeadLookup(null);
    } finally {
      setLookupLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(lookupTimerRef.current);
    if (leadConfirmed && number !== confirmedNumberRef.current) setLeadConfirmed(false);
    if (!number || number.replace(/[^\d+]/g, '').length < 7) { setLeadLookup(null); return; }
    lookupTimerRef.current = setTimeout(() => lookupLead(number), 600);
    return () => clearTimeout(lookupTimerRef.current);
  }, [number, lookupLead]); // intentionally omit leadConfirmed

  const openLeadForm = () => {
    if (!leadLookup) return;
    if (navigatingRef.current) return;
    navigatingRef.current = true;
    setTimeout(() => { navigatingRef.current = false; }, 2000);
    confirmedNumberRef.current = number;
    setLeadConfirmed(true);
    const alreadyOnForm = window.location.pathname.includes('/leads-form');
    if (leadLookup.found) {
      navigate('/agent/leads-form', {
        state: { leadId: leadLookup.lead.id, campaignId: leadLookup.lead.campaign || leadLookup.campaign_id },
        replace: alreadyOnForm,
      });
    } else {
      navigate('/agent/leads-form', {
        state: { campaignId: leadLookup.campaign_id, prefillPhone: leadLookup.phone },
        replace: alreadyOnForm,
      });
    }
  };

  // ── Audio helpers ──────────────────────────────────────────────────────────
  const unlockAudio = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
  };

  // Attach a single remote audio track to the <audio> sink and force playback.
  // Robust against: (a) audio element not mounted yet, (b) Chrome autoplay
  // rejection — in which case we stash the track and retry on the next gesture.
  const playRemote = () => {
    const el = remoteAudioRef.current;
    if (!el) return;
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err) => {
        // Autoplay blocked — surface it and retry on the next user interaction.
        console.warn('[DialerWidget] remote audio play() blocked, will retry on gesture:', err?.name || err);
        const retry = () => {
          remoteAudioRef.current?.play().catch(() => {});
          window.removeEventListener('pointerdown', retry);
          window.removeEventListener('keydown', retry);
        };
        window.addEventListener('pointerdown', retry, { once: true });
        window.addEventListener('keydown', retry, { once: true });
      });
    }
  };

  const attachRemoteTrack = (track) => {
    if (!track) return;
    pendingRemoteTrackRef.current = track;
    const el = remoteAudioRef.current;
    if (!el) {
      // Element not mounted yet; the mount effect will pick up the pending track.
      return;
    }
    const stream = new MediaStream([track]);
    el.srcObject = stream;
    el.muted = false;
    el.volume = 1;
    if (audioCtxRef.current?.state === 'suspended') {
      audioCtxRef.current.resume().then(playRemote).catch(playRemote);
    } else {
      playRemote();
    }
  };

  // Pull the audio track directly from the peer connection's receivers.
  const attachRemoteAudio = (pc) => {
    if (!pc) return;
    const audioReceiver = pc.getReceivers().find(r => r.track?.kind === 'audio');
    if (audioReceiver?.track) attachRemoteTrack(audioReceiver.track);
  };

  // ── postMessage listener ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === 'SET_DIAL_NUMBER' && e.data.number) setNumber(e.data.number);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // ── JsSIP connect ──────────────────────────────────────────────────────────
  const connectSIP = useCallback((cfg) => {
    try {
      const socket = new JsSIP.WebSocketInterface(cfg.ws_server);
      const ua = new JsSIP.UA({
        sockets: [socket],
        uri: cfg.sip_uri,
        password: cfg.password,
        display_name: cfg.display_name || cfg.extension,
      });

      activeUaRef.current = ua;

      ua.on('registered', () => {
        if (activeUaRef.current !== ua) return;
        setStatus('online'); statusRef.current = 'online';
        setErrorType(null);
      });

      ua.on('registrationFailed', (e) => {
        if (activeUaRef.current !== ua) return;
        const cause = e?.cause || '';
        // 401/403 = auth failure; other causes (e.g. Timeout) = transient
        if (cause === 'Unauthorized' || cause === 'Forbidden' || cause === '401' || cause === '403') {
          setErrorType('auth_failed');
        }
        setStatus('offline'); statusRef.current = 'offline';
      });

      ua.on('connected', () => {
        if (activeUaRef.current !== ua) return;
        setStatus('online'); statusRef.current = 'online';
        setErrorType(prev => (prev === 'disconnected') ? null : prev);
      });

      ua.on('disconnected', () => {
        if (activeUaRef.current !== ua) return;
        setStatus('offline'); statusRef.current = 'offline';
        setErrorType('disconnected');
      });

      ua.on('newRTCSession', (data) => {
        const session = data.session;
        sessionRef.current = session;

        session.on('peerconnection', (e) => {
          const pc = e.peerconnection;

          pc.addEventListener('track', (ev) => {
            if (ev.track.kind !== 'audio') return;
            const track = ev.streams?.[0]?.getAudioTracks()[0] ?? ev.track;
            if (track) attachRemoteTrack(track);
          });

          pc.addEventListener('iceconnectionstatechange', () => {
            if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
              const sender = pc.getSenders().find(s => s.track?.kind === 'audio');
              if (sender?.track) localStreamRef.current = sender.track;
            }
          });
        });

        if (data.originator === 'remote') {
          if (!canInboundRef.current) { try { session.terminate(); } catch (_) {} return; }
          const caller = session.remote_identity?.uri?.user || 'Unknown';
          setStatus('ringing');
          setIncoming(true);
          setIncomingFrom(caller);
          setNumber(caller);
          toneGenerator.unlock();
          toneGenerator.startRingtone();
        } else {
          setIncoming(false);
          setIncomingFrom('');
          session.on('progress', () => {
            setStatus('ringing');
            toneGenerator.startRingback();
          });
        }

        session.on('accepted', () => {
          toneGenerator.stopAll();
          setInCall(true);
          setStatus('in_call');
          setCallDuration(0);
          setErrorType(null);
          attachRemoteAudio(session.connection);
          durationIntervalRef.current = setInterval(() => setCallDuration(p => p + 1), 1000);
        });

        const resetCall = () => {
          toneGenerator.stopAll();
          sessionRef.current = null;
          localStreamRef.current = null;
          if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
          setInCall(false);
          setMuted(false);
          setOnHold(false);
          setIncoming(false);
          setIncomingFrom('');
          setStatus('online');
          setCallDuration(0);
          setLeadConfirmed(false);
          confirmedNumberRef.current = '';
          if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
        };

        session.on('ended', resetCall);

        session.on('failed', async (e) => {
          resetCall();
          const cause = e?.cause || '';

          // SIP response codes inside the cause string
          if (cause.includes('486') || cause === 'Busy') { setErrorType('busy'); return; }
          if (cause.includes('603') || cause.includes('487')) { setErrorType('rejected'); return; }

          const isMicCause =
            cause === 'User Denied Media Access' ||
            cause === 'Media Access Denied' ||
            cause === 'Media Not Found';

          if (!isMicCause) {
            // Any other cause (BYE, Timeout, ICE failure, etc.) — stay online
            setErrorType(null);
            return;
          }

          // Verify the mic is truly unavailable before alarming the agent.
          // These causes can fire on transient OS-level audio graph resets.
          const micResult = await checkMicAccess();
          if (micResult) setErrorType(micResult);
          // else: mic is fine — was transient, stay online
        });
      });

      uaRef.current = ua;
      ua.start();
    } catch (err) {
      console.error('[DialerWidget] connectSIP threw:', err);
      setErrorType('config_error');
      setStatus('offline');
    }
  }, []);

  // ── Boot (host only — embedded panel shares this connection) ───────────────
  useEffect(() => {
    if (!isHost) return undefined;

    const fetchConfig = async () => {
      mountIdRef.current += 1;
      const myMountId = mountIdRef.current;
      try {
        const response = await getWebRTCSIPConfig();
        if (myMountId !== mountIdRef.current) return;
        const data = response?.data?.data;
        if (data?.sip_uri && data?.ws_server) {
          setConfig(data);
          connectSIP(data);
        } else {
          setErrorType('config_error');
        }
      } catch {
        if (myMountId !== mountIdRef.current) return;
        setErrorType('config_error');
      }
    };
    fetchConfig();

    return () => {
      toneGenerator.stopAll();
      if (uaRef.current) { activeUaRef.current = null; try { uaRef.current.stop(); } catch (_) {} }
      if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
      if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (_) {} }
    };
  }, [isHost, connectSIP]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const dial = (digit) => {
    if (leadConfirmed) return;
    unlockAudio();
    toneGenerator.unlock();
    toneGenerator.playDtmf(digit);
    setNumber(prev => prev + digit);
  };

  const handleNumberChange = (e) => {
    if (leadConfirmed) return;
    setNumber(sanitisePhoneInput(e.target.value));
    if (errorType === 'invalid_number') setErrorType(null);
  };

  const placeCall = useCallback((dialNumber, campaignName = '') => {
    if (!uaRef.current || !dialNumber) return false;
    if (!isCallableNumber(dialNumber)) return false;
    setIncoming(false);
    setIncomingFrom('');
    unlockAudio();
    toneGenerator.unlock();
    toneGenerator.startRingback();
    const clean = dialNumber.replace(/[^\d+#*]/g, '');
    if (!clean) {
      toneGenerator.stopAll();
      return false;
    }
    const target = `sip:${clean}@${config?.sip_domain}`;
    setErrorType(null);
    const iceServers = Array.isArray(config?.ice_servers) && config.ice_servers.length > 0
      ? config.ice_servers
      : [{ urls: 'stun:stun.l.google.com:19302' }];
    uaRef.current.call(target, {
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers,
        rtcpMuxPolicy: 'require',
        bundlePolicy: 'max-bundle',
      },
      extraHeaders: campaignName ? [`X-Campaign-ID: ${campaignName}`] : [],
    });
    setStatus('dialing');
    window.dispatchEvent(new CustomEvent('dialerCallStarted', { detail: { number: clean } }));
    return true;
  }, [config]);

  const call = () => {
    if (embedded) {
      if (!isCallableNumber(number)) { setErrorType('invalid_number'); return; }
      if (!dialerActionsRef.current?.placeCall?.(number, leadLookup?.campaign_name || '')) {
        setErrorType('invalid_number');
      }
      return;
    }
    if (!uaRef.current || !number) return;
    if (!isCallableNumber(number)) { setErrorType('invalid_number'); return; }
    placeCall(number, leadLookup?.campaign_name || '');
  };

  const hangup = () => {
    toneGenerator.stopAll();
    if (embedded) { dialerActionsRef.current?.hangup?.(); return; }
    if (sessionRef.current) sessionRef.current.terminate();
  };

  const answerCall = () => {
    toneGenerator.stopAll();
    if (embedded) { dialerActionsRef.current?.answerCall?.(); return; }
    if (!sessionRef.current) return;
    unlockAudio();
    const iceServers = Array.isArray(config?.ice_servers) && config.ice_servers.length > 0
      ? config.ice_servers
      : [{ urls: 'stun:stun.l.google.com:19302' }];
    sessionRef.current.answer({
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers,
        rtcpMuxPolicy: 'require',
        bundlePolicy: 'max-bundle',
      },
    });
    setIncoming(false);

    // Auto-navigate to leads-form with the incoming caller's number
    const callerNumber = incomingFrom || '';
    if (callerNumber && callerNumber !== 'Unknown') {
      const e164Caller = toE164(callerNumber.replace(/[^\d+]/g, ''));
      if (e164Caller) {
        setNumber(e164Caller);
        lookupLead(e164Caller);
        const alreadyOnForm = window.location.pathname.includes('/leads-form');
        navigate('/agent/leads-form', {
          state: { prefillPhone: e164Caller, incomingCallAnswered: true },
          replace: alreadyOnForm,
        });
      }
    }
  };

  const rejectCall = () => {
    toneGenerator.stopAll();
    if (embedded) { dialerActionsRef.current?.rejectCall?.(); return; }
    if (sessionRef.current) { sessionRef.current.terminate(); }
    setIncoming(false); setIncomingFrom(''); setStatus('online');
  };

  const toggleMute = () => {
    if (embedded) { dialerActionsRef.current?.toggleMute?.(); return; }
    if (localStreamRef.current) {
      localStreamRef.current.enabled = muted;
      setMuted(m => !m);
    } else if (sessionRef.current) {
      if (muted) sessionRef.current.unmute({ audio: true });
      else sessionRef.current.mute({ audio: true });
      setMuted(m => !m);
    }
  };

  const toggleHold = () => {
    if (embedded) { dialerActionsRef.current?.toggleHold?.(); return; }
    if (!sessionRef.current) return;
    try {
      if (onHold) { sessionRef.current.unhold(); setOnHold(false); setStatus('in_call'); }
      else { sessionRef.current.hold(); setOnHold(true); setStatus('on_hold'); }
    } catch (_) {}
  };

  const clear = () => { if (!leadConfirmed) setNumber(prev => prev.slice(0, -1)); };

  const retryConnect = () => {
    const cfg = configRef.current;
    if (!cfg) { window.location.reload(); return; }
    if (uaRef.current) { activeUaRef.current = null; try { uaRef.current.stop(); } catch (_) {} }
    setErrorType(null);
    setStatus('offline');
    connectSIP(cfg);
  };

  // Expose host actions for embedded dialer panels
  useEffect(() => {
    if (!isHost) return undefined;
    dialerActionsRef.current = {
      placeCall: (dialNumber, campaignName) => {
        setIncoming(false);
        setIncomingFrom('');
        setNumber(dialNumber);
        return placeCall(dialNumber, campaignName);
      },
      hangup,
      answerCall,
      rejectCall,
      toggleMute,
      toggleHold,
      retryConnect,
    };
    return () => { dialerActionsRef.current = {}; };
  }, [isHost, placeCall, hangup, answerCall, rejectCall, toggleMute, toggleHold, retryConnect, dialerActionsRef]);

  const formatDuration = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const effectiveStatus = embedded ? ctxCallStatus : status;
  const effectiveInCall = embedded
    ? (ctxCallStatus === 'in_call' || ctxCallStatus === 'on_hold')
    : inCall;
  const effectiveIncoming = embedded
    ? (ctxIsIncoming && !effectiveInCall)
    : incoming;
  const effectiveIncomingFrom = embedded
    ? (incomingFrom || (ctxIsIncoming && ctxCallNumber ? ctxCallNumber : 'Unknown'))
    : incomingFrom;
  const effectiveNumber = embedded ? (ctxCallNumber || number) : number;
  const effectiveCallDuration = embedded ? ctxCallDuration : callDuration;
  const effectiveMuted = embedded ? ctxCallMuted : muted;
  const effectiveOnHold = embedded ? ctxCallStatus === 'on_hold' : onHold;

  // ── Status helpers ─────────────────────────────────────────────────────────
  const dotColor =
    effectiveStatus === 'online' ? 'bg-green-500' :
    effectiveStatus === 'in_call' ? 'bg-blue-500' :
    effectiveStatus === 'on_hold' ? 'bg-yellow-500' :
    (effectiveStatus === 'dialing' || effectiveStatus === 'ringing') ? 'bg-yellow-400 animate-pulse' :
    errorType ? 'bg-orange-500' : 'bg-gray-400';

  const statusLabel =
    effectiveStatus === 'online' ? 'Ready' :
    effectiveStatus === 'in_call' ? 'In Call' :
    effectiveStatus === 'on_hold' ? 'On Hold' :
    effectiveStatus === 'dialing' ? 'Dialing…' :
    (effectiveStatus === 'ringing' && effectiveIncoming) ? 'Incoming Call' :
    effectiveStatus === 'ringing' ? 'Ringing…' :
    errorType === 'auth_failed' ? 'Auth Error' :
    errorType === 'config_error' ? 'Config Error' :
    errorType === 'disconnected' ? 'Reconnecting…' :
    errorType === 'mic_blocked' ? 'Mic Blocked' :
    errorType === 'no_mic' ? 'No Mic' : 'Offline';

  const canCall = effectiveStatus === 'online' || errorType === 'mic_blocked' || errorType === 'no_mic';

  // The HOST instance is the one that owns the JsSIP session and receives the
  // remote audio track (see connectSIP / newRTCSession). Previously it returned
  // null, so `remoteAudioRef.current` was null on the host and the incoming
  // (far-end) audio track had no <audio> element to play through — Asterisk sent
  // RTP, the browser received it, but nothing was ever rendered to a sink.
  // Render a hidden audio element on the host so remote audio actually plays.
  if (isHost) {
    return (
      <audio
        ref={(el) => {
          remoteAudioRef.current = el;
          // If a remote track arrived before this element mounted, attach it now.
          if (el && pendingRemoteTrackRef.current) {
            attachRemoteTrack(pendingRemoteTrackRef.current);
          }
        }}
        autoPlay
        playsInline
        style={{ position: 'fixed', top: -9999, left: -9999, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="w-full h-full bg-white dark:bg-gray-900">
      {/* Status Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${dotColor}`} />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{statusLabel}</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
          <Phone className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">{config?.extension || 'N/A'}</span>
        </div>
      </div>

      {/* Error banner */}
      {errorType && (
        <ErrorBanner
          type={errorType}
          onDismiss={() => { setErrorType(null); if (status !== 'online') setStatus('online'); }}
          onRetry={embedded ? () => dialerActionsRef.current?.retryConnect?.() : retryConnect}
        />
      )}

      {/* Display & Lead Lookup */}
      {!effectiveInCall && !effectiveIncoming && effectiveStatus !== 'dialing' && effectiveStatus !== 'ringing' && (
        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <input
              type="text"
              value={number}
              onChange={handleNumberChange}
              onFocus={unlockAudio}
              placeholder="+1 555 000 0000"
              readOnly={leadConfirmed}
              className={`w-full px-4 py-3 text-base text-gray-900 dark:text-white bg-white dark:bg-gray-800 border rounded-lg outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 transition-colors ${
                leadConfirmed
                  ? 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed'
                  : errorType === 'invalid_number'
                    ? 'bg-white dark:bg-gray-800 border-red-400 dark:border-red-600 focus:ring-2 focus:ring-red-100 dark:focus:ring-red-900/50'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/50'
              }`}
            />
            {number && !leadConfirmed && (
              <button onClick={clear} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                <X className="w-4 h-4" />
              </button>
            )}
            {leadConfirmed && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>

          {/* Lead lookup */}
          {number.replace(/[^\d+]/g, '').length >= 7 && (
            <div className="mt-2">
              {lookupLoading && !leadConfirmed ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="w-3.5 h-3.5 border-2 border-gray-300 dark:border-gray-600 border-t-gray-500 dark:border-t-gray-400 rounded-full animate-spin flex-shrink-0" />
                  <span className="text-xs text-gray-400 dark:text-gray-500">Looking up number…</span>
                </div>
              ) : leadLookup?.found ? (
                <button
                  onClick={!leadConfirmed ? openLeadForm : undefined}
                  disabled={leadConfirmed}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 border rounded-lg transition-colors text-left ${
                    leadConfirmed ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 cursor-default' : 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 border-green-200 dark:border-green-800'
                  }`}
                >
                  <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
                    {(leadLookup.lead.full_name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-green-800 dark:text-green-300 truncate">{leadLookup.lead.full_name || 'Lead found'}</p>
                    <p className="text-[11px] text-green-600 dark:text-green-400">{leadConfirmed ? '✓ Lead confirmed — ready to call' : 'Tap to open lead form'}</p>
                  </div>
                  <svg className={`w-3.5 h-3.5 flex-shrink-0 ${leadConfirmed ? 'text-green-600 dark:text-green-400' : 'text-green-500 dark:text-green-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={leadConfirmed ? 'M5 13l4 4L19 7' : 'M9 5l7 7-7 7'} />
                  </svg>
                </button>
              ) : leadLookup && !leadLookup.found ? (
                <button
                  onClick={!leadConfirmed ? openLeadForm : undefined}
                  disabled={leadConfirmed}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 border rounded-lg transition-colors text-left ${
                    leadConfirmed ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 cursor-default' : 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 border-blue-200 dark:border-blue-800'
                  }`}
                >
                  <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-white">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-blue-800 dark:text-blue-300">No lead found</p>
                    <p className="text-[11px] text-blue-600 dark:text-blue-400">{leadConfirmed ? '✓ Confirmed — ready to call' : 'Tap to create new lead'}</p>
                  </div>
                  <svg className={`w-3.5 h-3.5 flex-shrink-0 ${leadConfirmed ? 'text-blue-600 dark:text-blue-400' : 'text-blue-500 dark:text-blue-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={leadConfirmed ? 'M5 13l4 4L19 7' : 'M9 5l7 7-7 7'} />
                  </svg>
                </button>
              ) : null}
            </div>
          )}

          {/* Dialpad toggle */}
          <button
            onClick={() => { if (!leadConfirmed) setShowDialpad(p => !p); }}
            disabled={leadConfirmed}
            className={`mt-2 w-full flex items-center justify-center gap-1 py-1 text-xs transition-colors ${
              leadConfirmed ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
          >
            <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${showDialpad ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            {showDialpad ? 'Hide dialpad' : 'Show dialpad'}
          </button>
        </div>
      )}

      {/* Outbound Dialing/Ringing View */}
      {!effectiveInCall && !effectiveIncoming && (effectiveStatus === 'dialing' || effectiveStatus === 'ringing') && (
        <div className="px-4 pt-3 pb-2 flex-shrink-0">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-5 text-center border border-blue-200 dark:border-blue-800">
            <div className="w-14 h-14 bg-blue-600 rounded-full mx-auto mb-3 flex items-center justify-center shadow-lg">
              <Phone className="w-7 h-7 text-white animate-pulse" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-0.5">
              {effectiveStatus === 'dialing' ? 'Dialing…' : 'Ringing…'}
            </p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{effectiveNumber || 'Unknown'}</p>
          </div>
        </div>
      )}

      {/* In-Call View */}
      {effectiveInCall && (
        <div className="px-4 pt-3 pb-2">
          <div className={`rounded-xl p-6 text-center border transition-colors duration-300 ${effectiveOnHold ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg ${effectiveOnHold ? 'bg-yellow-400' : 'bg-blue-600'}`}>
              {effectiveOnHold ? <Pause className="w-8 h-8 text-white" /> : <Phone className="w-8 h-8 text-white" />}
            </div>
            <p className="text-base font-semibold text-gray-900 dark:text-white mb-1">{effectiveNumber}</p>
            {effectiveOnHold && <p className="text-xs font-medium text-yellow-600 dark:text-yellow-400 mb-1">On Hold — MOH playing</p>}
            <p className={`text-3xl font-bold tabular-nums mt-1 ${effectiveOnHold ? 'text-yellow-500 dark:text-yellow-400' : 'text-blue-600 dark:text-blue-400'}`}>{formatDuration(effectiveCallDuration)}</p>
          </div>

          {/* Lead info chip during call — lets agent navigate to form mid-call */}
          {leadLookup && !leadConfirmed && (
            <button
              onClick={openLeadForm}
              className="w-full mt-2 flex items-center gap-2.5 px-3 py-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors text-left"
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold ${leadLookup.found ? 'bg-green-500' : 'bg-blue-500'}`}>
                {leadLookup.found ? (leadLookup.lead.full_name || '?')[0].toUpperCase() : '+'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                  {leadLookup.found ? (leadLookup.lead.full_name || 'Lead found') : 'New lead'}
                </p>
              </div>
              <span className="text-[10px] text-gray-400 dark:text-gray-500">Open form →</span>
            </button>
          )}
        </div>
      )}

      {/* Incoming Call */}
      {effectiveIncoming && !effectiveInCall && (
        <div className="px-4 pt-3 pb-2">
          <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center border border-green-200 dark:border-green-800">
            <div className="w-12 h-12 bg-green-600 rounded-full mx-auto mb-2 flex items-center justify-center">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Incoming Call</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{effectiveIncomingFrom}</p>
            <div className="flex gap-3">
              <button onClick={rejectCall} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2 font-medium">
                <PhoneOff className="w-4 h-4" /><span>Reject</span>
              </button>
              <button onClick={answerCall} className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2 font-medium">
                <Phone className="w-4 h-4" /><span>Answer</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialpad */}
      {!effectiveInCall && !effectiveIncoming && effectiveStatus !== 'dialing' && effectiveStatus !== 'ringing' && showDialpad && !leadConfirmed && (
        <div className="grid grid-cols-3 gap-2.5 px-4 pb-2">
          {[['1',''],['2','ABC'],['3','DEF'],['4','GHI'],['5','JKL'],['6','MNO'],['7','PQRS'],['8','TUV'],['9','WXYZ'],['*',''],['0','+'],['#','']].map(([digit, letters]) => (
            <button key={digit} onClick={() => dial(digit)} className="h-16 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 border border-gray-200 dark:border-gray-700 rounded-lg flex flex-col items-center justify-center transition-colors shadow-sm">
              <span className="text-2xl font-semibold text-gray-900 dark:text-white">{digit}</span>
              {letters && <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{letters}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="px-4 pb-3 pt-2">
        {effectiveIncoming && !effectiveInCall ? null : (
          !effectiveInCall && (effectiveStatus === 'dialing' || effectiveStatus === 'ringing') ? (
            <button onClick={hangup} className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2 font-semibold transition-colors shadow-sm">
              <PhoneOff className="w-4 h-4" /><span>Cancel</span>
            </button>
          ) : !effectiveInCall ? (
            leadConfirmed ? (
              <button
                onClick={call}
                disabled={!number || !canCall || !canOutbound}
                className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center gap-2 font-semibold transition-colors shadow-sm"
              >
                <Phone className="w-4 h-4" /><span>Call</span>
              </button>
            ) : (
              <div className="w-full py-3 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 rounded-lg flex items-center justify-center gap-2 text-sm select-none">
                <Phone className="w-4 h-4" />
                <span>{number.replace(/[^\d+]/g, '').length >= 7 ? 'Select Lead Found / No Lead Found to call' : 'Enter a number to continue'}</span>
              </div>
            )
          ) : (
            <div className="flex gap-3">
              <button onClick={toggleMute} className={`flex-1 py-3 rounded-lg font-medium transition-colors shadow-sm ${effectiveMuted ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'}`}>
                {effectiveMuted ? <MicOff className="w-5 h-5 mx-auto" /> : <Mic className="w-5 h-5 mx-auto" />}
              </button>
              <button onClick={toggleHold} className={`flex-1 py-3 rounded-lg font-medium transition-colors shadow-sm ${effectiveOnHold ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'}`}>
                {effectiveOnHold ? <Play className="w-5 h-5 mx-auto" /> : <Pause className="w-5 h-5 mx-auto" />}
              </button>
              <button onClick={hangup} className="flex-[2] py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2 font-semibold transition-colors shadow-sm">
                <PhoneOff className="w-4 h-4" /><span>End Call</span>
              </button>
            </div>
          )
        )}
      </div>

      <audio
        ref={remoteAudioRef}
        autoPlay
        playsInline
        style={{ position: 'fixed', top: -9999, left: -9999, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
    </div>
  );
};

export default DialerWidget;
